import { type User, UserManager } from 'oidc-client-ts';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import {
  clearNativeReturnUrl,
  isValidPostLoginReturnUrl,
  rememberNativeReturnUrl,
} from '@/shared/auth/postLoginRedirect';
import { getCognitoLocalMetadataSeed } from './cognitoLocalEmulator';
import { nativeTokensToUser, type NativeTokens } from './nativeCognito';
import { getReturnUrlFromOidcUser } from './postLoginRedirect';

type UserManagerEventHandlers = {
  onUserLoaded: (user: User) => void;
  onUserUnloaded: () => void;
  onSilentRenewError: () => void;
  onAccessTokenExpired: () => void;
};

/** Singleton instance of the UserManager.  */
let cognitoUserManager: UserManager | null = null;
let cognitoConfigKey: string | null = null;
let userManagerEventsWired = false;
let sessionVersion = 0;
let userChanges: Promise<unknown> = Promise.resolve();
let nativeRefresh: Promise<User | null> | null = null;

function changeUser<T>(operation: () => Promise<T>): Promise<T> {
  const result = userChanges.then(operation, operation);
  userChanges = result.catch(() => undefined);
  return result;
}

async function removeUser(): Promise<void> {
  if (!isNativeCognitoEnabled) return getCognitoUserManager().removeUser();
  sessionVersion++;
  clearNativeReturnUrl();
  await changeUser(() => getCognitoUserManager().removeUser());
}

async function importNativeSession(
  tokens: NativeTokens,
  version: number,
  returnUrl?: string,
): Promise<User | null> {
  const user = await nativeTokensToUser(tokens, returnUrl);
  return changeUser(async () => {
    if (version !== sessionVersion) return null;
    const manager = getCognitoUserManager();
    await manager.storeUser(user);
    if (version !== sessionVersion) return null;
    rememberNativeReturnUrl(user, returnUrl);
    await manager.events.load(user, false);
    return user;
  });
}

function signinSilent(): Promise<User | null> {
  if (!isNativeCognitoEnabled) return getCognitoUserManager().signinSilent();
  if (nativeRefresh) return nativeRefresh;
  const version = sessionVersion;
  nativeRefresh = changeUser(async () => {
    const manager = getCognitoUserManager();
    if (version !== sessionVersion) return null;
    const current = await manager.getUser();
    // API sign-in has no managed-login cookie: never fall back to an iframe.
    if (!current?.refresh_token) throw new Error('Please sign in again');
    try {
      const refreshed = await manager.signinSilent();
      if (version !== sessionVersion) {
        await manager.removeUser();
        return null;
      }
      if (!refreshed) throw new Error('Please sign in again');
      const user = await nativeTokensToUser(
        {
          accessToken: refreshed.access_token,
          idToken: refreshed.id_token ?? '',
          refreshToken: refreshed.refresh_token ?? current.refresh_token,
        },
        getReturnUrlFromOidcUser(current) ?? undefined,
      );
      if (version !== sessionVersion) {
        await manager.removeUser();
        return null;
      }
      await manager.storeUser(user);
      await manager.events.load(user, false);
      return user;
    } catch (error) {
      await manager.removeUser();
      throw error;
    }
  }).finally(() => {
    nativeRefresh = null;
  });
  return nativeRefresh;
}

/**
 * Only matters during local development (or when changing .env settings in production back to non-cognito).
 * If we change which Cognito server the app uses (e.g. local emulator vs staging),
 * this makes sure login uses the new settings instead of the old ones.
 * Without it, we may need a full page refresh after changing .env.
 */
function resetCognitoUserManagerIfConfigChanged(): void {
  const _env = import.meta.env;
  const _domain = _env.VITE_IDP_COGNITO_DOMAIN;
  const _clientId = _env.VITE_IDP_COGNITO_CLIENT_ID;
  const configKey = `${_domain}|${_clientId}`;

  if (cognitoUserManager !== null && cognitoConfigKey !== configKey) {
    cognitoUserManager = null;
    userManagerEventsWired = false; // reset the events so we don't have multiple handlers
  }

  cognitoConfigKey = configKey;
}

function getCognitoUserManager(): UserManager {
  if (!isCognitoEnabled) {
    throw new Error('Cognito authentication is not enabled');
  }

  if (import.meta.env.DEV) {
    resetCognitoUserManagerIfConfigChanged();
  }

  if (!cognitoUserManager) {
    cognitoUserManager = createCognitoUserManager();
  }
  return cognitoUserManager;
}

function createCognitoUserManager(): UserManager {
  const authority = import.meta.env.VITE_IDP_COGNITO_DOMAIN as string;
  const localMetadataSeed = getCognitoLocalMetadataSeed(authority);
  const isLocalEmulator = localMetadataSeed !== undefined;

  return new UserManager({
    authority,
    client_id: import.meta.env.VITE_IDP_COGNITO_CLIENT_ID as string,
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: 'code',
    scope: 'openid email profile aws.cognito.signin.user.admin',
    // cognito-local: verify OAuth/PKCE/callback only; avoid silent iframe renew against the stub
    automaticSilentRenew: !isLocalEmulator && !isNativeCognitoEnabled,
    monitorSession: !isLocalEmulator && !isNativeCognitoEnabled,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    silentRequestTimeoutInSeconds: 15,
    maxSilentRenewTimeoutRetries: 3,
    ...(localMetadataSeed ? { metadataSeed: localMetadataSeed } : {}),
  });
}

function loginWithReturnUrl(returnUrl?: string): Promise<void> {
  const args =
    returnUrl && isValidPostLoginReturnUrl(returnUrl) ? { state: { returnUrl } } : undefined;
  return getCognitoUserManager().signinRedirect(args);
}

async function signoutRedirect(): Promise<void> {
  const userManager = getCognitoUserManager();
  await userManager.signoutRedirect({
    extraQueryParams: {
      client_id: userManager.settings.client_id,
      logout_uri: `${window.location.origin}/logout`,
    },
  });
}

function setupUserManagerEvents(handlers: UserManagerEventHandlers): void {
  if (userManagerEventsWired) {
    return;
  }
  userManagerEventsWired = true;

  const { events } = getCognitoUserManager();
  // Native completion/refresh owns hydration and fencing; OIDC still owns storage/timers.
  events.addUserLoaded((user) => {
    if (!isNativeCognitoEnabled) handlers.onUserLoaded(user);
  });
  events.addUserUnloaded(() => {
    if (!isNativeCognitoEnabled) handlers.onUserUnloaded();
  });
  events.addSilentRenewError(() => {
    if (!isNativeCognitoEnabled) handlers.onSilentRenewError();
  });
  events.addAccessTokenExpired(handlers.onAccessTokenExpired);
}

/**
 * A service wrapper for OIDC operations.
 */
export const cognitoClient = {
  login: (returnUrl?: string) => loginWithReturnUrl(returnUrl),
  signinCallback: (url?: string) => {
    if (!isNativeCognitoEnabled) return getCognitoUserManager().signinCallback(url);
    const version = sessionVersion;
    return changeUser(async () => {
      const user = await getCognitoUserManager().signinCallback(url);
      if (version !== sessionVersion) {
        await getCognitoUserManager().removeUser();
        return null;
      }
      if (user) rememberNativeReturnUrl(user, getReturnUrlFromOidcUser(user) ?? undefined);
      return user;
    });
  },
  handleCallback: () => getCognitoUserManager().signinRedirectCallback(),
  getUser: async () => {
    if (isNativeCognitoEnabled) await userChanges;
    return getCognitoUserManager().getUser();
  },
  signinSilent,
  removeUser,
  importNativeSession,
  sessionVersion: () => sessionVersion,
  signoutRedirect,
  setupUserManagerEvents,
};

export function getCognitoRegion(): string {
  const region = import.meta.env.VITE_IDP_COGNITO_REGION;
  if (!region || typeof region !== 'string') {
    throw new Error('VITE_IDP_COGNITO_REGION is not configured');
  }
  return region;
}
